import {
  TReceivedReferralData,
  TReceivedReferralDetailsData,
} from "types/automated-referrals.type";
import {
  TGrsPendingLead,
  TGrsPendingLeadDetail,
} from "types/grs-pending-lead.type";
import { getUserFullName } from "utils/account-user.utils";

const trimToNull = (value: string | null) => value?.trim() || null;

export const toGrsPendingLead = (
  referral: TReceivedReferralData
): TGrsPendingLead => ({
  id: referral.id,
  firstName: referral.firstName,
  lastName: referral.lastName,
  town: trimToNull(referral.town),
  postcode: trimToNull(referral.postcode),
  createdAt: referral.createdAt,
});

export const formatGrsAddressLabel = ({
  town,
  postcode,
}: Pick<TGrsPendingLead, "town" | "postcode">) => {
  if (town && postcode) return `${town}, ${postcode}`;
  return town || postcode || "Address hidden until accepted";
};

export const toGrsPendingLeadDetail = (
  referral: TReceivedReferralDetailsData
): TGrsPendingLeadDetail => ({
  ...toGrsPendingLead(referral),
  bedrooms: referral.bedrooms,
  moveReason: referral.moveReasonOther || referral.moveReason,
  agentNotes: trimToNull(referral.agentNotes),
  alreadyOnMarket: referral.alreadyOnMarket,
});

export const buildGrsPropertyDetailLines = (detail: TGrsPendingLeadDetail) => {
  const lines: string[] = [];

  if (detail.bedrooms != null) {
    lines.push(`Number of bedrooms: ${detail.bedrooms}`);
  }
  if (detail.town || detail.postcode) {
    lines.push(`Address: ${formatGrsAddressLabel(detail)}`);
  }

  return lines;
};

export const buildGrsAgentNotesLines = (detail: TGrsPendingLeadDetail) => {
  const lines: string[] = [];

  if (detail.agentNotes) lines.push(detail.agentNotes);
  if (detail.moveReason) lines.push(`Move reason: ${detail.moveReason}`);
  if (detail.alreadyOnMarket != null) {
    lines.push(
      `Current property: ${
        detail.alreadyOnMarket ? "On the market" : "Off the market"
      }`
    );
  }

  return lines;
};

export const formatGrsPendingLeadName = ({
  firstName,
  lastName,
}: Pick<TGrsPendingLead, "firstName" | "lastName">) =>
  getUserFullName(trimToNull(firstName), trimToNull(lastName)) ||
  "Unnamed lead";

export const formatGrsPendingLeadsAnnouncement = (count: number) => {
  if (!count) return "";

  return `${count} GRS ${
    count === 1 ? "lead is" : "leads are"
  } waiting for your decision`;
};
